import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { run } from "./AdminListPanel";
import { db } from "@/api/base44Client";
import { ProfessionSubjectSelect } from "./ProfessionSubjectSelect";
import { QuestionRow } from "./QuestionRow";

export function QuestionDatabase() {
  const [prof, setProf] = useState("");
  const [subject, setSubject] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const qc = useQueryClient();
  const refetch = () => qc.invalidateQueries({ queryKey: ["admin-questions"] });
  const { data, isLoading } = useQuery({
    queryKey: ["admin-questions", prof, subject, q],
    queryFn: () => db.admin.listQuestions({ profession: prof, subject, q, limit: 200 }),
  });
  const items = data?.items || [];
  useEffect(() => { setSel(new Set()); }, [prof, subject, q]);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((i) => sel.has(i.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(items.map((i) => i.id)));

  const bulkDelete = () => {
    if (!window.confirm(`${sel.size} Fragen wirklich löschen?`)) return;
    run(async () => {
      const r = await db.admin.deleteQuestions([...sel]);
      toast.success(`${r.deleted} Fragen gelöscht`);
      setSel(new Set()); refetch();
    });
  };

  const deleteAll = async () => {
    const total = data?.total ?? 0;
    if (!window.confirm(`WIRKLICH ALLE ${total} Fragen löschen? Das kann nicht rückgängig gemacht werden.`)) return;
    if (!window.confirm(`Letzte Bestätigung: Alle ${total} Fragen für ${prof || "alle Berufe"}/${subject || "alle Fächer"} löschen?`)) return;
    setDeleting(true);
    let deleted = 0;
    try {
      while (true) {
        const batch = await db.admin.listQuestions({ profession: prof, subject, q, limit: 200 });
        if (!batch?.items?.length) break;
        const ids = batch.items.map((i) => i.id);
        const r = await db.admin.deleteQuestions(ids);
        deleted += r.deleted || ids.length;
        toast.info(`${deleted} von ${total} gelöscht…`);
        if (batch.items.length < 200) break;
      }
      toast.success(`${deleted} Fragen gelöscht`);
      setSel(new Set()); refetch();
    } catch (e) {
      toast.error("Fehler beim Löschen: " + (e?.message || "unbekannt"));
    } finally { setDeleting(false); }
  };

  return (
    <section className="adm-card" data-testid="admin-question-db">
      <div className="adm-card-head">
        <h2 className="adm-card-title"><span className="adm-card-icon"><Database className="h-4 w-4" /></span>Fragen-Datenbank</h2>
        <span className="adm-pill" data-testid="admin-question-total">{data?.total ?? 0} Fragen</span>
      </div>
      <div className="adm-grid adm-grid-3" style={{ marginBottom: "1rem" }}>
        <ProfessionSubjectSelect allowAll profession={prof} subject={subject} onChange={(p, s) => { setProf(p); setSubject(s); }} testPrefix="admin-qdb" />
        <label className="adm-field"><span className="adm-label">Suche</span>
          <div style={{ position: "relative" }}>
            <Search className="h-4 w-4" style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", opacity: 0.6 }} />
            <input className="adm-input" style={{ paddingLeft: "2.2rem" }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Frage suchen…" data-testid="admin-qdb-search" />
          </div>
        </label>
      </div>
      {items.length > 0 && (
        <div className="adm-bulkbar" data-testid="admin-qdb-bulkbar">
          <label><input type="checkbox" className="adm-check" checked={allSelected} onChange={toggleAll} data-testid="admin-qdb-select-all" /> Alle auswählen</label>
          <span className="adm-muted" data-testid="admin-qdb-selected-count">{sel.size} ausgewählt</span>
          <button className="adm-btn adm-btn-sm adm-btn-danger-solid" style={{ marginLeft: "auto" }} disabled={!sel.size} onClick={bulkDelete} data-testid="admin-qdb-bulk-delete">
            <Trash2 className="h-3.5 w-3.5" /> Ausgewählte löschen
          </button>
          <button className="adm-btn adm-btn-sm adm-btn-danger-solid" disabled={deleting || !data?.total} onClick={deleteAll} style={{ background: "#7f1d1d" }}>
            <Trash2 className="h-3.5 w-3.5" /> {deleting ? "Löscht…" : `Alle ${data?.total ?? 0} löschen`}
          </button>
        </div>
      )}
      <div className="adm-list" style={{ maxHeight: "36rem" }}>
        {isLoading && <p className="adm-empty">Lädt…</p>}
        {!isLoading && items.length === 0 && <p className="adm-empty">Keine Fragen gefunden.</p>}
        {items.map((it) => <QuestionRow key={it.id} q={it} onChange={refetch} selected={sel.has(it.id)} onToggle={toggle} />)}
      </div>
      {data && data.total > items.length && <p className="adm-muted" style={{ marginTop: "0.75rem" }}>Zeige {items.length} von {data.total} – Filter verwenden, um weitere zu sehen.</p>}
    </section>
  );
}
